import { Link, type LinkProps } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Card, Loading, Typography } from "@/deps/ui-library";
import { cn } from "@/lib/cn";

export interface Kpi {
  label: string;
  value: string;
  /** A short qualifier under the figure — "3 overdue", "since Shrawan". */
  detail?: string;
  /** Where the figure comes from. A tile that cannot be opened is a figure nobody can check. */
  to?: LinkProps["to"];
  search?: LinkProps["search"];
  tone?: "default" | "warn" | "bad";
}

const TILE = "grid grid-cols-2 gap-3 md:grid-cols-4";

/**
 * The row of figures at the top of a screen: RDS `Card` tiles.
 *
 * Every tile links somewhere. A KPI you cannot click is a number somebody has to trust — and the
 * first thing anyone does with an unexpected total is try to see what is in it.
 */
export function KpiStrip({ items, loading = false }: { items: Kpi[]; loading?: boolean }) {
  if (loading) {
    return (
      <div className={TILE} aria-busy="true">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i} size="sm" aria-hidden="true">
            <Loading.Skeleton variant="text" className="h-3 w-2/3" />
            <Loading.Skeleton variant="rounded" className="mt-2 h-7 w-1/2" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={TILE}>
      {items.map((kpi) => {
        const body = (
          <>
            <Typography.Text as="span" variant="small" weight="medium" muted className="block">
              {kpi.label}
            </Typography.Text>
            <span
              className={cn(
                "num mt-1 block text-2xl font-semibold",
                kpi.tone === "bad"
                  ? "text-destructive"
                  : kpi.tone === "warn"
                    ? "text-warn-text"
                    : "text-foreground",
              )}
            >
              {kpi.value}
            </span>
            {kpi.detail ? (
              <Typography.Text as="span" variant="small" muted className="mt-0.5 block">
                {kpi.detail}
              </Typography.Text>
            ) : null}
          </>
        );

        return kpi.to ? (
          <Link
            key={kpi.label}
            to={kpi.to}
            search={kpi.search}
            className="focus-visible:ring-ring block rounded-lg no-underline focus-visible:ring-2 focus-visible:outline-none"
          >
            <Card size="sm" variant="interactive" className="h-full">
              {body}
            </Card>
          </Link>
        ) : (
          <Card key={kpi.label} size="sm">
            {body}
          </Card>
        );
      })}
    </div>
  );
}

/** A single figure with a label, for use outside the strip. */
export function KpiValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Typography.Text as="span" variant="small" weight="medium" muted className="block">
        {label}
      </Typography.Text>
      <span className="num text-foreground mt-0.5 block text-sm font-medium">{children}</span>
    </div>
  );
}
